import React, { memo, useImperativeHandle, forwardRef } from 'react'
import PropTypes from 'prop-types'

import { CheckBox as BaseCheckBox } from '../Controls'
import { handlePostFormFieldData, useFormBase, formImperativeHandleFunctions } from './common'
import { useTranslation } from 'react-i18next'
/**
 * Implements Mui CheckBox for forms.
 *
 * @param {object} props component props
 * @returns {CheckBox} CheckBox component
 */
const CheckBox = memo(forwardRef((props, ref) => {
  const { t } = useTranslation()
  const { fieldTemplate, data, label, ...finalProps } = props

  const { onChange, disabled, setDisabled, fieldLabel, refFormElement, valueRef, setErrorMsg } = 
    useFormBase(props, fieldTemplate, data)

  useImperativeHandle(ref, () => ({
    ...formImperativeHandleFunctions(fieldTemplate, refFormElement, valueRef, setErrorMsg, setDisabled),
    validate: (postObject, refs) => {
      return { value: handlePostFormFieldData(postObject, fieldTemplate, valueRef.current, refs) }
    },
  }))

  return (
    <BaseCheckBox
      {...finalProps}
      disabled={finalProps.disabled !== undefined ? finalProps.disabled : disabled}
      label={fieldLabel.current && t(`${fieldLabel.current}`)}
      ref={refFormElement}
      onChange={onChange}
      noTranslate={true}
    />
  )
}))

CheckBox.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  /**
   Default value for the control.
   */ 
  defaultValue: PropTypes.bool,
  /**
   Label to display for the control.
   */ 
  label: PropTypes.string,
  /**
   onChange function called when the checkbox is clicked.
   */
  onChange: PropTypes.func,
  /**
   Html color for the checkbox.
   */
  checkColor: PropTypes.string,
  /**
   MUI color for the checkbox.
   */
  color: PropTypes.oneOf(['primary', 'secondary', 'default']),
  /**
   Prevents the language translation of the label.
   */
  noTranslate: PropTypes.bool,
  /**
   Determines if the control is disabled.
   */
  disabled: PropTypes.bool,
  /**
   Field definition template for the control.
   */
  fieldTemplate: PropTypes.object.isRequired,
  /**
   The data object retrieved from the backend which contains all data for the form controls.
   */
  data: PropTypes.object,
  /**
   Set a ref to the component in your page and call this function to restore its default value contained in the fieldTemplate prop.
   */
  'ref.current.clear': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to get its current value.
   */
  'ref.current.getValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to set its current value.
   */
  'ref.current.setValue': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to run the validators in the fieldTemplate prop.
   */
  'ref.current.validate': PropTypes.func,
  /**
   Set a ref to the component in your page and call this function to disable the control.
   */
  'ref.current.setDisabled': PropTypes.func,
  /**
   Returns the form template associated with this form control.
   */
  'ref.current.getFormTemplate': PropTypes.func,
}

export default CheckBox
